'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { salariesAPI, exportAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { TableSkeleton } from '../components/LoadingSkeleton';
import { handleExport } from '../utils/exportHelper';
import '../styles/Salaries.css';

function Salaries() {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'employee'

  useEffect(() => {
    fetchSalaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const fetchSalaries = async () => {
    setLoading(true);
    try {
      const response = await salariesAPI.getByMonth(month);
      setSalaries(response.data.salaries || []);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to fetch salaries';
      showError(message);
      setSalaries([]);
    } finally {
      setLoading(false);
    }
  };

  // Group salaries by date (for daily view)
  const salariesByDate = useMemo(() => {
    const grouped = {};
    salaries.forEach(salary => {
      const dateKey = new Date(salary.workDate).toISOString().split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          salaries: [],
          totalAmount: 0,
          totalMembers: 0,
          paidAmount: 0,
          unpaidAmount: 0,
          paidCount: 0,
          unpaidCount: 0
        };
      }
      const headcount = salary.headcount || 1;
      grouped[dateKey].salaries.push(salary);
      grouped[dateKey].totalAmount += salary.amount || 0;
      grouped[dateKey].totalMembers += headcount;
      if (salary.isPaid) {
        grouped[dateKey].paidAmount += salary.amount || 0;
        grouped[dateKey].paidCount += 1;
      } else {
        grouped[dateKey].unpaidAmount += salary.amount || 0;
        grouped[dateKey].unpaidCount += 1;
      }
    });
    return grouped;
  }, [salaries]);

  const dailyList = useMemo(() => {
    return Object.values(salariesByDate).sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
  }, [salariesByDate]);

  // Group salaries by employee (for employee view)
  const salariesByEmployee = useMemo(() => {
    const grouped = {};
    salaries.forEach(salary => {
      const empId = salary.employeeId?._id || salary.employeeId;
      if (!grouped[empId]) {
        grouped[empId] = {
          employee: salary.employeeId,
          salaries: [],
          totalAmount: 0,
          paidAmount: 0,
          unpaidAmount: 0,
          paidCount: 0,
          unpaidCount: 0
        };
      }
      grouped[empId].salaries.push(salary);
      grouped[empId].totalAmount += salary.amount || 0;
      if (salary.isPaid) {
        grouped[empId].paidAmount += salary.amount || 0;
        grouped[empId].paidCount += 1;
      } else {
        grouped[empId].unpaidAmount += salary.amount || 0;
        grouped[empId].unpaidCount += 1;
      }
    });
    return grouped;
  }, [salaries]);

  const employeeList = useMemo(() => {
    return Object.values(salariesByEmployee).sort((a, b) => 
      (a.employee?.name || '').localeCompare(b.employee?.name || '')
    );
  }, [salariesByEmployee]);

  const handleTogglePaid = async (salaryId, currentStatus) => {
    try {
      await salariesAPI.update(salaryId, { isPaid: !currentStatus });
      success(`Salary marked as ${!currentStatus ? 'paid' : 'unpaid'}`);
      fetchSalaries();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update salary status';
      showError(message);
    }
  };

  const handleMarkAllPaid = async (employeeId) => {
    const employeeSalaries = salariesByEmployee[employeeId];
    if (!employeeSalaries || employeeSalaries.unpaidCount === 0) return;

    if (!window.confirm(`Mark all unpaid salaries (₹${employeeSalaries.unpaidAmount.toFixed(2)}) as paid for ${employeeSalaries.employee?.name}?`)) {
      return;
    }

    try {
      await Promise.all(
        employeeSalaries.salaries
          .filter(s => !s.isPaid)
          .map(s => salariesAPI.update(s._id, { isPaid: true }))
      );
      success('All salaries marked as paid');
      fetchSalaries();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to mark salaries as paid';
      showError(message);
    }
  };

  const handleDelete = async (salaryId, employeeName) => {
    if (!window.confirm(`Remove this salary entry for "${employeeName}"?`)) {
      return;
    }
    try {
      await salariesAPI.delete(salaryId);
      success('Salary entry removed successfully');
      fetchSalaries();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to remove salary entry';
      showError(message);
    }
  };

  // Calculate monthly totals
  const monthlyTotals = useMemo(() => {
    let totalAmount = 0;
    let paidAmount = 0;
    let unpaidAmount = 0;
    let totalEmployees = employeeList.length;
    let paidEmployees = 0;

    employeeList.forEach(emp => {
      totalAmount += emp.totalAmount;
      paidAmount += emp.paidAmount;
      unpaidAmount += emp.unpaidAmount;
      if (emp.unpaidCount === 0 && emp.paidCount > 0) {
        paidEmployees += 1;
      }
    });

    return {
      totalAmount,
      paidAmount,
      unpaidAmount,
      totalEmployees,
      paidEmployees,
      unpaidEmployees: totalEmployees - paidEmployees
    };
  }, [employeeList]);


  return (
    <div className="salaries-container">
      <div className="page-header">
        <h1>Employee Salaries</h1>
        <div className="header-actions">
          <div className="view-mode-toggle">
            <button
              onClick={() => setViewMode('daily')}
              className={`btn btn-small ${viewMode === 'daily' ? 'btn-primary' : 'btn-secondary'}`}
            >
              📅 Daily View
            </button>
            <button
              onClick={() => setViewMode('employee')}
              className={`btn btn-small ${viewMode === 'employee' ? 'btn-primary' : 'btn-secondary'}`}
            >
              👤 Employee View
            </button>
          </div>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="month-input"
          />
          {salaries.length > 0 && (
            <button
              onClick={() => handleExport(
                () => exportAPI.exportMonthlyReport(month),
                `salaries_${month}.xlsx`,
                () => success('Salaries exported successfully'),
                (error) => showError(error)
              )}
              className="btn btn-secondary"
            >
              📥 Export to Excel
            </button>
          )}
        </div>
      </div>

      {/* Monthly Summary */}
      {!loading && salaries.length > 0 && (
        <div className="monthly-summary">
          <div className="summary-card">
            <div className="summary-label">Total Days</div>
            <div className="summary-value">{dailyList.length}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Members</div>
            <div className="summary-value">{dailyList.reduce((sum, day) => sum + day.totalMembers, 0)}</div>
          </div>
          <div className="summary-card">
            <div className="summary-label">Total Salary</div>
            <div className="summary-value">₹{monthlyTotals.totalAmount.toFixed(2)}</div>
          </div>
          <div className="summary-card success">
            <div className="summary-label">Paid</div>
            <div className="summary-value">₹{monthlyTotals.paidAmount.toFixed(2)}</div>
            <div className="summary-subtext">{monthlyTotals.paidEmployees} employees</div>
          </div>
          <div className="summary-card warning">
            <div className="summary-label">Unpaid</div>
            <div className="summary-value">₹{monthlyTotals.unpaidAmount.toFixed(2)}</div>
            <div className="summary-subtext">{monthlyTotals.unpaidEmployees} employees</div>
          </div>
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={5} columns={6} />
      ) : viewMode === 'daily' ? (
        // Daily View - Show each day with members and totals
        dailyList.length > 0 ? (
          <div className="salaries-list">
            {dailyList.map((dayData) => (
              <div key={dayData.date} className="daily-salary-card">
                <div className="daily-salary-header">
                  <div className="daily-date-info">
                    <div className="daily-date">
                      {new Date(dayData.date).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <div className="daily-stats">
                      <span className="members-count">
                        👥 {dayData.totalMembers} member{dayData.totalMembers !== 1 ? 's' : ''}
                      </span>
                      <span>•</span>
                      <span>{dayData.salaries.length} employee{dayData.salaries.length !== 1 ? 's' : ''}</span>
                      {dayData.unpaidCount > 0 && (
                        <>
                          <span>•</span>
                          <span className="unpaid-text">{dayData.unpaidCount} unpaid</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="daily-totals">
                    <div className="total-amount">₹{dayData.totalAmount.toFixed(2)}</div>
                    <div className="amount-breakdown">
                      {dayData.paidAmount > 0 && (
                        <span className="paid-amount">Paid: ₹{dayData.paidAmount.toFixed(2)}</span>
                      )}
                      {dayData.unpaidAmount > 0 && (
                        <span className="unpaid-amount">Unpaid: ₹{dayData.unpaidAmount.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="daily-employees-list">
                  {dayData.salaries
                    .sort((a, b) => (a.employeeId?.name || '').localeCompare(b.employeeId?.name || ''))
                    .map((salary) => {
                      const emp = salary.employeeId;
                      const headcount = salary.headcount || 1;
                      return (
                        <div key={salary._id} className="daily-employee-entry">
                          <div className="daily-employee-info">
                            <span className="employee-name">{emp?.name || 'Unknown'}</span>
                            <span className="employee-role">{emp?.role || 'N/A'}</span>
                            {headcount > 1 && (
                              <span className="headcount-badge">Team: x{headcount}</span>
                            )}
                            <span className="employee-event">{salary.eventName || 'N/A'}</span>
                          </div>
                          <div className="daily-employee-amount">
                            <span className="salary-amount">₹{salary.amount.toFixed(2)}</span>
                            {headcount > 1 && (
                              <span className="per-person">(₹{(salary.amount / headcount).toFixed(2)}/person)</span>
                            )}
                          </div>
                          <div className="daily-employee-actions">
                            <span className={`status status-${salary.isPaid ? 'paid' : 'unpaid'}`}>
                              {salary.isPaid ? '✓ Paid' : '○ Unpaid'}
                            </span>
                            {user?.role === 'owner' && (
                              <>
                                <button
                                  onClick={() => handleTogglePaid(salary._id, salary.isPaid)}
                                  className={`btn btn-small ${salary.isPaid ? 'btn-secondary' : 'btn-success'}`}
                                >
                                  {salary.isPaid ? 'Unpaid' : 'Paid'}
                                </button>
                                <button
                                  onClick={() => handleDelete(salary._id, emp?.name)}
                                  className="btn btn-danger btn-small"
                                >
                                  Remove
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-data">
            No salary entries found for {new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}. Assign employees to events to generate salary entries.
          </div>
        )
      ) : employeeList.length > 0 ? (
        <div className="salaries-list">
          {employeeList.map((empData) => {
            const emp = empData.employee;
            const isExpanded = selectedEmployeeId === emp?._id;
            
            return (
              <div key={emp?._id} className="employee-salary-card">
                <div 
                  className="employee-salary-header"
                  onClick={() => setSelectedEmployeeId(isExpanded ? null : emp?._id)}
                >
                  <div className="employee-info">
                    <div className="employee-name-row">
                      <span className="employee-name">{emp?.name || 'Unknown'}</span>
                      <span className="employee-role">{emp?.role || 'N/A'}</span>
                      <span className={`category-badge category-${emp?.category || 'other'}`}>
                        {emp?.category || 'other'}
                      </span>
                    </div>
                    <div className="employee-stats">
                      <span>{empData.salaries.length} day(s)</span>
                      <span>•</span>
                      <span>
                        Total Members: {empData.salaries.reduce((sum, s) => sum + (s.headcount || 1), 0)}
                      </span>
                      <span>•</span>
                      <span className={empData.unpaidCount > 0 ? 'unpaid-text' : 'paid-text'}>
                        {empData.unpaidCount} unpaid
                      </span>
                    </div>
                  </div>
                  <div className="employee-totals">
                    <div className="total-amount">₹{empData.totalAmount.toFixed(2)}</div>
                    <div className="amount-breakdown">
                      <span className="paid-amount">Paid: ₹{empData.paidAmount.toFixed(2)}</span>
                      {empData.unpaidAmount > 0 && (
                        <span className="unpaid-amount">Unpaid: ₹{empData.unpaidAmount.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <div className="expand-icon">{isExpanded ? '▲' : '▼'}</div>
                </div>

                {isExpanded && (
                  <div className="employee-salary-details">
                    {empData.unpaidCount > 0 && user?.role === 'owner' && (
                      <div className="bulk-action">
                        <button
                          onClick={() => handleMarkAllPaid(emp?._id)}
                          className="btn btn-success btn-small"
                        >
                          ✓ Mark All as Paid (₹{empData.unpaidAmount.toFixed(2)})
                        </button>
                      </div>
                    )}
                    <div className="salary-entries">
                      {empData.salaries
                        .sort((a, b) => new Date(b.workDate) - new Date(a.workDate))
                        .map((salary) => (
                          <div key={salary._id} className="salary-entry">
                            <div className="salary-entry-info">
                              <div className="salary-date">
                                {new Date(salary.workDate).toLocaleDateString()}
                              </div>
                              <div className="salary-event">
                                {salary.eventName || salary.eventId?.eventName || 'N/A'} {salary.headcount > 1 ? `(x${salary.headcount})` : ''}
                              </div>
                              <div className="salary-amount">₹{salary.amount.toFixed(2)}</div>
                            </div>
                            <div className="salary-entry-actions">
                              <span className={`status status-${salary.isPaid ? 'paid' : 'unpaid'}`}>
                                {salary.isPaid ? '✓ Paid' : '○ Unpaid'}
                              </span>
                              {user?.role === 'owner' && (
                                <>
                                  <button
                                    onClick={() => handleTogglePaid(salary._id, salary.isPaid)}
                                    className={`btn btn-small ${salary.isPaid ? 'btn-secondary' : 'btn-success'}`}
                                  >
                                    {salary.isPaid ? 'Mark Unpaid' : 'Mark Paid'}
                                  </button>
                                  <button
                                    onClick={() => handleDelete(salary._id, emp?.name)}
                                    className="btn btn-danger btn-small"
                                  >
                                    Remove
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="no-data">
          {salaries.length === 0 
            ? `No salary entries found for ${new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}. Assign employees to events to generate salary entries.`
            : 'No employees found.'
          }
        </div>
      )}
    </div>
  );
}

export default Salaries;
